import logger from '../../utils/logger';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalStateContext } from '../../utils/GlobalStateContext';
import { getUserId, sanitizeDateForInput } from '../../utils/utils';
import '../../style/RISFollowUpNotesModal.css';

const statusColors = {
  Pending:  { background: '#fef3c7', color: '#92400e' },
  Final:    { background: '#d1fae5', color: '#065f46' },
  Addendum: { background: '#dbeafe', color: '#1e40af' },
};

const RISFollowUpNotesModal = ({ isOpen, onClose }) => {
  const { currentUser, currentPatient } = useContext(GlobalStateContext);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const user = getUserId(currentUser);
  const navigate = useNavigate();

  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const patientName = currentPatient
    ? `${currentPatient.first_name} ${currentPatient.last_name}`
    : null;

  useEffect(() => {
    if (isOpen && currentPatient?.ris_patients_id) {
      fetchNotes();
    }
  }, [isOpen, currentPatient]); // eslint-disable-line

  const fetchNotes = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/orders/risFollowUpNotes?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser?.type}`
      );
      const data = await res.json();
      if (res.ok) setNotes(data);
    } catch (err) {
      logger.error('Error fetching follow-up notes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenNote = (note) => {
    onClose();
    navigate(`/risFollowUpNote?editing=true&note_id=${note.id}`);
  };

  if (!isOpen) return null;

  return (
    <div className="rfnm-backdrop" onClick={onClose}>
      <div className="rfnm-modal" onClick={e => e.stopPropagation()}>
        <div className="rfnm-header">
          <div>
            <h2 className="rfnm-title">Follow-Up Notes</h2>
            {patientName && <p className="rfnm-patient">{patientName}</p>}
          </div>
          <button className="rfnm-close" onClick={onClose}>×</button>
        </div>

        <div className="rfnm-body">
          {!currentPatient ? (
            <p className="rfnm-empty">No patient selected. Please select a patient first.</p>
          ) : isLoading ? (
            <p className="rfnm-empty">Loading notes...</p>
          ) : notes.length === 0 ? (
            <p className="rfnm-empty">No follow-up notes found for this patient.</p>
          ) : (
            <ul className="rfnm-list">
              {notes.map(note => {
                const badge = statusColors[note.status] || statusColors.Pending;
                return (
                  <li
                    key={note.id}
                    className="rfnm-item"
                    onClick={() => handleOpenNote(note)}
                  >
                    <div className="rfnm-item-left">
                      <span className="rfnm-item-date">
                        {note.visit_date
                          ? sanitizeDateForInput(note.visit_date)
                          : sanitizeDateForInput(note.created_at)}
                      </span>
                      <span className="rfnm-item-clinician">{note.clinician_name || '—'}</span>
                    </div>
                    <div className="rfnm-item-right">
                      <span
                        className="rfnm-item-status"
                        style={{ background: badge.background, color: badge.color }}
                      >
                        {note.status}
                      </span>
                      {(note.status === 'Final' || note.status === 'Addendum') && (
                        <span className="rfnm-addendum-hint">+ Add Addendum</span>
                      )}
                      <span className="rfnm-item-arrow">›</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default RISFollowUpNotesModal;
